import { BLOCKS } from '@contentful/rich-text-types';
import { format } from 'date-fns';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import get from 'lodash.get';
import React from 'react';
import StickyBox from 'react-sticky-box';

import BlogSeo from '../components/Blog/BlogSeo';
import { ArrowLink } from '../components/Common/Links';
import PageSection from '../components/Common/PageSection';
import RichTextArticle from '../components/Common/RichText/RichTextArticle';
import ScrollTo, { smoothScrollToRef } from '../components/Common/ScrollTo';
import TableOfContents from '../components/Common/TableOfContents/TableOfContents';
import Accordion from '../components/Generic/Accordion';
import AdditionalResourceCard from '../components/Generic/AdditionalResourceCard';
import StickyNav from '../components/Generic/StickyNav';
import Layout, { BACKGROUND } from '../components/Layouts/Layout';
import { getUrl } from '../tools/mappers';
import * as styles from './pillarPage.module.css';

class PillarPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      sectionRefs: [],
    };
    this.backToTopRef = React.createRef();
    this.handleScroll = this.handleScroll.bind(this);
  }

  componentDidMount() {
    typeof window !== 'undefined' && window.addEventListener('scroll', this.handleScroll);
    const {
      data: { page },
    } = this.props;

    const { hash } = this.props.location;

    if (typeof window !== 'undefined' && hash) {
      window.requestAnimationFrame(() => {
        const anchor = document.querySelector(hash);
        if (!anchor) return;
        const offset = anchor.getBoundingClientRect().top + window.scrollY;

        window.scroll({ top: offset, left: 0 });
      });
    }

    let sections = [];
    if (page && page.tableOfContents && page.tableOfContents.group) {
      if (typeof window === 'undefined' || !document) {
        return;
      }
      // get all with tag
      // store sectionId with topPosition
      // apply end at found next section
      // find article bottom
      const sectionRefs = document.querySelectorAll('[data-section-num]');
      const article = document.getElementById('article');
      const header = window.document.getElementById('hero-section');
      const headerHeight = header.offsetHeight / 2;
      sectionRefs.forEach((section, i) => {
        let offsetTop = section.offsetTop; //getElemDistance(section);
        let offsetBottom;
        if (sectionRefs[i + 1]) {
          offsetBottom = sectionRefs[i + 1].offsetTop; //getElemDistance(sectionRefs[i + 1]);
        } else {
          const bounds = article.getBoundingClientRect();
          offsetBottom = article.offsetTop + bounds.height; //getElemDistance(article) + bounds.height;
        }

        const item = page.tableOfContents.group.find((item) => item.sectionId === section.id);
        sections.push({
          id: section.id,
          title: item.title,
          offsetTop: offsetTop + headerHeight,
          offsetBottom: offsetBottom + headerHeight,
          ref: section,
        });
      });
    }
    this.setState({ sectionRefs: sections });
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll() {
    //const headerHeight = 30;

    const scrollPosition = window.scrollY + 30;
    const selected = this.state.sectionRefs.find((ele) => {
      if (ele) {
        const { offsetBottom, offsetTop } = ele;
        //const view = window.innerHeight / 2;
        /*console.log(`
          view: ${view}
          id: ${ele.id}
          scrollPos: ${scrollPosition},
          top: ${offsetTop},
          bottom: ${offsetBottom}
        `);*/
        // TODO: apply view to when offsets are gathered

        return scrollPosition > offsetTop && scrollPosition < offsetBottom;
      }
      return false;
    });

    if (selected && selected.id !== this.state.visibleSection) {
      this.setState({ visibleSection: selected.id });
    } else if (!selected && this.state.visibleSection) {
      this.setState({ visibleSection: undefined });
    }
  }

  render() {
    const {
      data: { page },
      location,
    } = this.props;

    // Handle return to top
    const onScrollCta = (e) => {
      e.preventDefault();
      smoothScrollToRef(this.backToTopRef);
    };

    const updated = format(new Date(page.updatedAt), 'MMMM dd, yyyy');
    const readTime = page.readTime;

    // Handle hero image
    const imageData = get(page, 'heroImage.gatsbyImageData');
    let hero;
    hero = !!imageData ? (
      <GatsbyImage image={imageData} alt={page.title} />
    ) : (
      <img alt={page.title} src={get(page, 'heroImage.file.url', '')} />
    );

    const { ogImage, ...rest } = get(page, 'pageMetaData', {}) || {};
    const imageBackup = get(ogImage, 'resize.src') || get(ogImage, 'file.url') || get(page, 'heroImage.file.url');
    const meta = {
      ogImageUrl: imageBackup,
      ...rest,
    };

    let tableOfContents = [];
    if (page.tableOfContents && page.tableOfContents.group) {
      tableOfContents = page.tableOfContents.group.map((item) => {
        return {
          title: item.title,
          url: `#${item.sectionId}`,
          id: item.sectionId,
        };
      });
    }

    let additionalResources = [];
    if (page.additionalResources) {
      additionalResources = page.additionalResources.map((item) => {
        return {
          title: item.title,
          url: getUrl(item),
        };
      });
    }

    const articleOverrides = {
      renderNode: {
        [BLOCKS.HEADING_2]: (node, children) => (
          <>
            <div className={styles.headingRule} />
            <h2>{children}</h2>
          </>
        ),
      },
    };

    return (
      <Layout background={BACKGROUND.WHITE} className={styles.pillarPage} pageMeta={page.pageMetaData}>
        <BlogSeo meta={meta} slug={location.pathname} />
        <StickyNav
          displayNav={true}
          visibleSection={this.state.visibleSection}
          title={get(page, 'tableOfContents.title')}
          items={tableOfContents}
        />
        <PageSection isTopSection slim id={'hero-section'}>
          <ScrollTo reference={this.backToTopRef} />
          <div className={styles.header}>
            <div className={styles.hero}>{hero}</div>
            <div className={styles.heroContent}>
              <div className={styles.contentHead}>
                {readTime && <span className={styles.readTime}>{readTime}</span>}
                <hr />
                {get(page, 'contentType.title')}
              </div>
              <div>
                <h1>{page.heading}</h1>
                <p>{get(page, 'headingLede.headingLede')}</p>
              </div>
              <div className={styles.lastItem}>
                <span className={styles.lastUpdated}>Last Updated: {updated}</span>
              </div>
            </div>
          </div>
        </PageSection>
        <PageSection slim>
          <div className={styles.articleGrid}>
            <div className={styles.sidebar}>
              <StickyBox offsetTop={10} offsetBottom={10}>
                {tableOfContents && (
                  <TableOfContents
                    localLinks={true}
                    title={'Table of Contents'}
                    currentSection={this.state.visibleSection}
                    items={tableOfContents}
                  />
                )}
                {additionalResources && (
                  <TableOfContents card title={'Additional Resources'} items={additionalResources} />
                )}
              </StickyBox>
            </div>

            <div className={styles.article} id={'article'}>
              <RichTextArticle
                copy={page.pageContent}
                className={styles.articleOverrides}
                optionsOverride={articleOverrides}
                inlineCta={page.inlineCtaType}
                customInlineCta={{ copy: page.inlineCtaCustomCopy, link: page.inlineCtaCustomLink }}
              />
              <ArrowLink
                className={styles.arrowAdjust}
                label={'Return to top'}
                onClick={onScrollCta}
                isArrowUp={true}
              />
            </div>
          </div>
        </PageSection>
        {page.additionalSections &&
          page.additionalSections.map((section, i) => {
            if (section.__typename === 'ContentfulItemGrouping' && section.group && section.group.length > 0) {
              if (section.type === 'FAQ') {
                return (
                  <PageSection key={i} slim>
                    <div className={styles.faqHeading}>
                      <h3>{section.heading}</h3>
                    </div>
                    <Accordion items={section.group} />
                  </PageSection>
                );
              }
              if (section.type === 'Resources') {
                return (
                  <PageSection className={styles.grey} slim key={i}>
                    <div className={styles.related}>
                      {section.group.map((post, j) => {
                        let updated;
                        if (post.__typename === 'ContentfulBlogPage') {
                          updated = {
                            ...post,
                            title: post.heading,
                            description: { description: post.headingLede?.headingLede },
                          };
                        } else {
                          updated = { ...post };
                        }

                        return <AdditionalResourceCard post={updated} key={j} />;
                      })}
                    </div>
                  </PageSection>
                );
              }
            }
            return null;
          })}
      </Layout>
    );
  }
}

export default PillarPage;

export const pageQuery = graphql`
  query GetPillarPage($slug: String!) {
    page: contentfulBlogPage(slug: { eq: $slug }) {
      heading
      headingLede {
        headingLede
      }
      heroImage {
        file {
          url
        }
      }
      publishDate
      updatedAt
      tableOfContents {
        heading
        group {
          ... on ContentfulJumpLink {
            id
            title
            sectionId
          }
        }
      }
      pageMetaData {
        ... on Node {
          ...BlogMeta
        }
      }
      updatedAt
      additionalResources {
        ... on Node {
          ... on ContentfulBlogPost {
            title
            slug
            __typename
          }
        }
        ... on Node {
          ... on ContentfulResourceCenterPost {
            title
            slug
            __typename
          }
        }
      }
      additionalSections {
        ... on Node {
          ... on ContentfulItemGrouping {
            __typename
            id
            type
            heading
            group {
              ... on Node {
                ... on ContentfulBlogPost {
                  ...BlogPost
                }
              }
              ... on Node {
                ...ResourceCenterPost
              }
              ... on Node {
                ... on ContentfulBlogPage {
                  contentful_id
                  __typename
                  pageMeta: pageMetaData {
                    ...BlogMeta
                  }
                  contentType {
                    title
                    slug
                    viewLabel
                  }
                  heroImage {
                    gatsbyImageData(placeholder: BLURRED)
                    file {
                      url
                      contentType
                    }
                    title
                  }
                  heading
                  headingLede {
                    headingLede
                  }
                  title
                  slug
                }
              }
              ... on Node {
                ... on ContentfulBlogSimpleContentBlock {
                  __typename
                  id
                  title
                  supportingText {
                    raw
                  }
                }
              }
            }
          }
        }
      }
      contentType {
        title
        viewLabel
        slug
      }
      readTime
      pageContent {
        raw
        references {
          ... on Node {
            ... on ContentfulAsset {
              ...ContentfulBlogImage
            }
          }
          ... on Node {
            ... on ContentfulGithubGist {
              id
              contentful_id
              name
              gistUrl
              __typename
              internal {
                type
              }
            }
          }
          ... on Node {
            ... on ContentfulVideoEmbed {
              id
              contentful_id
              videoUrl
              __typename
              internal {
                type
              }
            }
          }
          ... on Node {
            ... on ContentfulBlogSectionCalloutEmbed {
              id
              contentful_id
              borderStyle
              sectionContent {
                raw
              }
              internal {
                type
              }
              __typename
            }
          }
          ... on Node {
            ... on ContentfulArticleImage {
              id
              contentful_id
              __typename
              internal {
                type
              }
              image {
                ... on ContentfulAsset {
                  ...ContentfulBlogImage
                }
              }
              alignImage
              url
              caption
            }
          }
          ... on Node {
            ... on ContentfulJumpLink {
              id
              title
              sectionId
              contentful_id
              __typename
              internal {
                type
              }
            }
          }
          ... on Node {
            ... on ContentfulCallToAction {
              ...CallToAction
            }
          }
        }
      }
      inlineCtaType
      inlineCtaCustomCopy
      inlineCtaCustomLink
    }
    blogSettings: contentfulBlogSettings(contentful_id: { eq: "6KdgnoVe8iL9MGRTTPgqyQ" }) {
      ...SiteSettings
    }
  }
`;
